import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Observable, Subscription, forkJoin, interval, of, timer } from 'rxjs';
import { catchError, map, switchMap, tap } from 'rxjs/operators';
import { ConfigurationManagerService } from './configuration-manager.service';
import moment from 'moment';
import { ArcAngelService } from './arc-angel.service';
import { GlobalSettingsService } from './global-settings.service';
import { ApiService } from './api.service';
import { OrganizationUtilService } from './organization-util.service';

@Injectable({
  providedIn: 'root'
})
export class MessagingService {
  private pollSub?: Subscription;
  organizations: any[] = [];
  data: any;
  constructor(
    private http: HttpClient,
    private arcAngelService: ArcAngelService,
    private globalSettingsService: GlobalSettingsService,
    private api: ApiService,
     private orgUtil: OrganizationUtilService
  ) { }
  messageListFull: any[] = [];
  numberTotal = 0;
  lastMessageTime: string | null = null;
  private firstPollerCalled = false;

  stopPolling() {
    this.pollSub?.unsubscribe();
  }

  ngOnDestroy() {
    this.stopPolling();
  }

  // Example: Get messages for user
  getMessagesForUser(userId: string, limit = 20, page = 1, date?: string): Observable<any> {
    const url =
      ConfigurationManagerService.settings.BaseURL +
      ConfigurationManagerService.settings.GetMessage;
    return this.http.get<any>(url)

    
  }

  // Example: Mark messages as read
  markMessagesAsRead(ids: number[]): Observable<any> {
    return forkJoin(ids.map(id =>
      this.http.patch(`${ConfigurationManagerService.settings.TezAddPapiBaseUrl}messages/${id}/read`, { readAt: new Date().toISOString() })
    )).pipe(
      catchError(error => of(error))
    );
  }

  getNewMessagesForOrgs(
    orgIds: number[],
    methods: string[] = ['SMS', 'NOTIFICATION'],
    limit: number = 10000,
    page: number = 1
  ): Observable<any> {
    const url =
      ConfigurationManagerService.settings.BaseURL +
      ConfigurationManagerService.settings.MessagewithHeader;

    const body = {
      messageMemoWhere: {
        toOrganizationIds: { $overlap: orgIds }
      },
      messageHeaderWhere: {
        readAt: null
      },
      where: {
        methods: {
          $overlap: [
            "SMS",
            // "NOTIFICATION"
          ]
        }
      },
      limit,
      page
    };

    return this.api.post<any>(url, body);
  }

  getToOrgMessageHeaders(messageIds: string[]) {
    const url =
      ConfigurationManagerService.settings.BaseURL +
      ConfigurationManagerService.settings.MessagewithHeader;
    const body = {
      order: [['createdAt', 'ASC']],
      messageId: messageIds,
      direction: 'TO',
      entity: 'ORGANIZATION'
    };
    return this.api.post<any>(url, body);

  }

  getNewMessagesForUserAndOrg(
    orgIds:  number[],
    userIds: number[],
    methods: string[] = ['SMS'],
    limit: number = 10000,
    page: number = 1
  ): Observable<any> {
    const url =
      ConfigurationManagerService.settings.BaseURL +
      ConfigurationManagerService.settings.MessagewithHeader;

    const body = {
      limit,
      page,
      messageMemoWhere: {
        $or: [
          {
            fromOrganizationIds: { $overlap: orgIds },
            toUserIds: { $overlap: userIds }
          },
          {
            toOrganizationIds: { $overlap: orgIds },
            fromUserIds: { $overlap: userIds }
          }
        ]
      },
      where: {
        methods: { $overlap: methods }
      }
    };

    return this.api.post<any>(url, body);
  }


  // Replace this with real HttpClient call to your backend
  // private papiRequest(id: string, db: string, action: string, body: any, options: any = {}) {
  //   // Example: you might replace with HttpClient POST
  //   return this.http.post(`/api/${db}/${action}`, { id, ...body, options });
  // }
  // Add other methods as needed, following the above pattern

  // Example: Send org message (adapt as needed)
  // sendOrgMessage(options: {
  //   fromOrgId: number,
  //   toUserId: number[],
  //   fromUserId: number,
  //   methods: string[],
  //   subject: string,
  //   text: string,
  //   html: string,
  //   file?: File
  // }): Observable<any> {
  //   const body = {
  //     fromOrgId: options.fromOrgId,
  //     toUserId: options.toUserId,
  //     fromUserId: options.fromUserId,
  //     methods: options.methods,
  //     subject: options.subject,
  //     text: options.text,
  //     html: options.html,
  //     // file: options.file // handle file upload separately if needed
  //   };
  //   return this.http.post(`${this.apiUrl}/messages/org/send`, body).pipe(
  //     catchError(error => of(error))
  //   );
  // }


  // You’ll want to set these values from outside (component)
  toUserId!: string;
  numberPerPage = 20;

  /**
   * Get read messages
   */
  getReadMessages(orgIds: number[], limit: number, page: number): Observable<any> {
    const url =
      ConfigurationManagerService.settings.BaseURL +
      ConfigurationManagerService.settings.MessagewithHeader;

    const body = {
      messageMemoWhere: {
        toOrganizationIds: { $overlap: orgIds }
      },
      messageHeaderWhere: {
        readAt: { $ne: null }
      },
      limit,
      page
    };

    return this.api.post<any>(url, body).pipe(
      catchError(err => {
        console.error('Error fetching read messages:', err);
        return of({ rows: [], count: 0 });
      })
    );
  }

  /**
   * Get sent messages
   */
  getSentMessages(orgIds: number[], limit: number, page: number): Observable<any> {
    const url =
      ConfigurationManagerService.settings.BaseURL +
      ConfigurationManagerService.settings.MessagewithHeader;

    const body = {
      messageMemoWhere: {
        fromOrganizationIds: { $overlap: orgIds }
      },
      limit,
      page
    };

    return this.api.post<any>(url, body).pipe(
      catchError(err => {
        console.error('Error fetching sent messages:', err);
        return of({ rows: [], count: 0 });
      })
    );
  }
  /**
   * Get period messages for a specific user within a time range
   * @param userId User ID to get messages for
   * @param limit Number of messages to retrieve (default: 100)
   * @param page Page number (default: 1)
   * @param startTime Start time in ISO string format
   * @param endTime End time in ISO string format
   * @returns Observable with message data
   */
  getPeriodMessagesForUser(
    userId: string | number,
    limit: number = 100,
    page: number = 1,
    startTime: string,
    endTime: string
  ): Observable<any> {
    const url =
      ConfigurationManagerService.settings.BaseURL +
      ConfigurationManagerService.settings.MessagewithHeader;
    const orgIds = this.orgUtil?.myOrgs
    ? Object.values(this.orgUtil.myOrgs).map((org: any) => String(org.arcAngelId || org.id))
    : [];
   
    const body = {
      limit,
      page,
      messageMemoWhere: {
        $or: [
          {
            fromOrganizationIds: { $overlap: orgIds },
            toUserIds: { $overlap: [userId] }
          },
          {
            toOrganizationIds: { $overlap: orgIds },
            fromUserIds: { $overlap: [userId] }
          }
        ]
      },
      where: {
        methods: {
          $overlap: ['SMS', 'EMAIL', 'NOTIFICATION']
        },
        createdAt: {
          $between: [startTime, endTime]
        }
      }
    };

    return this.api.post<any>(url, body).pipe(
      catchError(err => {
        console.error('Error fetching period messages for user:', err);
        return of({ 
          rows: [], 
          count: 0 
        });
      })
    );
  }
  getMessageRecipients(messageId: string, direction: string = 'TO'): Observable<any> {
  const url =
    ConfigurationManagerService.settings.BaseURL +
    ConfigurationManagerService.settings.MessageHeaderFindAndCountAllFromOrganization;

  const body = {
    where: {
      messageId: messageId,
      direction: direction
    }
  };

  return this.api.post<any>(url, body).pipe(
    catchError(err => {
      console.error('Error fetching message recipients:', err);
      return of({ rows: [] }); // return safe fallback
    })
  );
}

  /**
     * Bulk create incident users using a payload object
     * @param payload Object containing messageId, incidentId, statuses, perimeterFilterType, fromOrganizationId, etc.
  */
  bulkCreateToIncidentUsers(payload: any): Observable<any> {
    const url =
      ConfigurationManagerService.settings.BaseURL +
      ConfigurationManagerService.settings.bulkCreateToIncidentUsers;

    return this.http.post<any>(url, payload).pipe(
      catchError((err) => {
        console.error('Error while bulk creating incident users:', err);
        // Return safe fallback
        return of({ success: false, message: 'Failed to create incident users' });
      })
    );
  }

  bulkCreateToOrganizationUsers(payload: any): Observable<any> {
    const url =
      ConfigurationManagerService.settings.BaseURL +
      ConfigurationManagerService.settings.bulkCreateToOrganizationUsers;

    return this.http.post<any>(url, payload).pipe(
      catchError((err) => {
        console.error('Error while bulk creating incident users:', err);
        // Return safe fallback
        return of({ success: false, message: 'Failed to create incident users' });
      })
    );
  }


}
